/**
 * Motion Presets
 * Reusable framer-motion variants for consistent animations.
 * All animations are subtle and professional.
 */

// Check for reduced motion preference
const prefersReducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const noMotion = {
    hidden: { opacity: 1 },
    visible: { opacity: 1 },
};

// ── Fade Up ──
// Content fades in while sliding up slightly
export const fadeUp = prefersReducedMotion
    ? noMotion
    : {
        hidden: {
            opacity: 0,
            y: 24,
            filter: 'blur(8px)',
        },
        visible: {
            opacity: 1,
            y: 0,
            filter: 'blur(0px)',
            transition: {
                duration: 0.72,
                ease: [0.16, 1, 0.3, 1],
            },
        },
    };

// ── Fade In ──
// Simple opacity fade
export const fadeIn = prefersReducedMotion
    ? noMotion
    : {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: [0.16, 1, 0.3, 1],
            },
        },
    };

// ── Stagger Container ──
// Parent variant that staggers children animations
export const staggerContainer = (staggerDelay = 0.1, delayChildren = 0.1) =>
    prefersReducedMotion
        ? noMotion
        : {
            hidden: { opacity: 1 },
            visible: {
                opacity: 1,
                transition: {
                    staggerChildren: staggerDelay,
                    delayChildren: delayChildren,
                },
            },
        };

// ── Project Grid Reveal ──
// Slower stagger for portfolio cards as they enter the viewport.
export const projectGridReveal = prefersReducedMotion
    ? noMotion
    : {
        hidden: { opacity: 1 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.1,
            },
        },
    };

export const projectCardReveal = prefersReducedMotion
    ? noMotion
    : {
        hidden: {
            opacity: 0,
            y: 34,
            scale: 0.975,
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                duration: 0.82,
                ease: [0.19, 1, 0.22, 1],
            },
        },
    };

// ── Hover Elevation ──
// Subtle lift effect on hover (for card-like components)
export const hoverElevation = prefersReducedMotion
    ? {}
    : {
        whileHover: {
            y: -4,
            transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
        },
        whileTap: {
            scale: 0.98,
            transition: { duration: 0.1 },
        },
    };

// ── Scale on Tap ──
// Button press feedback
export const scaleOnTap = prefersReducedMotion
    ? {}
    : {
        whileHover: { scale: 1.02 },
        whileTap: { scale: 0.97 },
    };

// ── Slide In from Left ──
export const slideInLeft = prefersReducedMotion
    ? noMotion
    : {
        hidden: { opacity: 0, x: -30 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.6,
                ease: [0.16, 1, 0.3, 1],
            },
        },
    };

// ── Modal Overlay ──
export const modalOverlay = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } },
    exit: { opacity: 0, transition: { duration: 0.15 } },
};

// ── Modal Content ──
export const modalContent = prefersReducedMotion
    ? {
        hidden: { opacity: 0 },
        visible: { opacity: 1 },
        exit: { opacity: 0 },
    }
    : {
        hidden: { opacity: 0, scale: 0.95, y: 20 },
        visible: {
            opacity: 1,
            scale: 1,
            y: 0,
            transition: { duration: 0.3, ease: [0.16, 1, 0.3, 1] },
        },
        exit: {
            opacity: 0,
            scale: 0.95,
            y: 10,
            transition: { duration: 0.15 },
        },
    };

// ── Nav Item ──
export const navItem = prefersReducedMotion
    ? noMotion
    : {
        hidden: { opacity: 0, y: -10 },
        visible: (i) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: i * 0.05,
                duration: 0.4,
                ease: [0.16, 1, 0.3, 1],
            },
        }),
    };
